import type { Locale } from '@/shared/store/localeStore'

import { helpPath, type HelpCategoryKey } from '../data/helpCategories'

export type HelpSchemaKind =
  | { kind: 'faq'; questions: Array<{ question: string; answer: string }> }
  | {
      kind: 'article'
      headline: string
      description: string
      key: HelpCategoryKey
      locale: Locale
    }

/**
 * Genera el objeto JSON-LD para una página del centro de ayuda.
 * El consumidor lo pasa a <SeoHead jsonLd={…}> (que ya lo serializa).
 *
 * Uso (FAQ):
 *   const schema = buildHelpSchema({ kind: 'faq', questions: [...] })
 *
 * Uso (Article — derives URL from key + locale):
 *   const schema = buildHelpSchema({
 *     kind: 'article', headline, description, key: 'reliability', locale,
 *   })
 */
export function buildHelpSchema(schema: HelpSchemaKind): object {
  const baseUrl = (import.meta.env.VITE_APP_URL as string | undefined) ?? ''
  if (schema.kind === 'faq') {
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: schema.questions.map((q) => ({
        '@type': 'Question',
        name: q.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: q.answer,
        },
      })),
    }
  }
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: schema.headline,
    description: schema.description,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${baseUrl}${helpPath(schema.key, schema.locale)}`,
    },
    author: { '@type': 'Organization', name: 'Match&Play' },
    publisher: {
      '@type': 'Organization',
      name: 'Match&Play',
      logo: { '@type': 'ImageObject', url: `${baseUrl}/og/logo.png` },
    },
    datePublished: '2026-06-08',
    dateModified: '2026-06-08',
  }
}
